import { useMemo, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Box,
  ChevronDown,
  Image,
  Palette,
  QrCode,
  Nfc,
  Type,
  Upload,
} from 'lucide-react';

import {
  normalizeQrEditorSchema,
} from '../admin/qrEditorSchema.jsx';

import '../../../styles/QrPrint/editor/QrDynamicCustomizationPanel.css';

const clamp = (value, min, max) =>
  Math.min(max, Math.max(min, Number(value) || min));

const cloneValue = (value) => {
  if (value === undefined) {
    return {};
  }

  return JSON.parse(JSON.stringify(value));
};

const getElement = (configuration, block) => {
  const existing =
    configuration?.editor_elements?.[block.id];

  if (existing !== undefined) {
    return existing;
  }

  return cloneValue(block.default ?? {});
};

const getOptionValue = (option) =>
  typeof option === 'object'
    ? option.value
    : option;

const getOptionLabel = (option) =>
  typeof option === 'object'
    ? option.label || option.value
    : option;

export default function QrDynamicCustomizationPanel({
  schema,
  configuration,
  onConfigurationChange,
  product,
  selectedSize,
  onSizeChange,
  selectedMaterial,
  onMaterialChange,
  selectedColor,
  onColorChange,
  threeDConfiguration,
  onThreeDChange,
  side = 'front',
}) {
  const normalized = useMemo(
    () => normalizeQrEditorSchema(schema),
    [schema],
  );

  const enabledBlocks = normalized.blocks.filter(
    (block) =>
      block.enabled !== false &&
      (block.side || 'front') === side,
  );

  const updateElement = (block, patch) => {
    const current = getElement(configuration, block);

    onConfigurationChange((previous) => ({
      ...previous,
      editor_elements: {
        ...(previous.editor_elements || {}),
        [block.id]:
          current &&
          typeof current === 'object' &&
          !Array.isArray(current)
            ? {
                ...current,
                ...patch,
              }
            : patch,
      },
    }));
  };

  const updateScalarElement = (block, value) => {
    onConfigurationChange((previous) => ({
      ...previous,
      editor_elements: {
        ...(previous.editor_elements || {}),
        [block.id]: value,
      },
    }));
  };

  return (
    <section className="qr-dynamic-panel">
      <div className="qr-dynamic-panel__heading">
        <span>{side === 'back' ? 'Back side' : 'Front side'}</span>
        <h2>Design elements</h2>
        <p>
          Every change is shown immediately in the preview.
        </p>
      </div>

      {product?.available_sizes?.length ? (
        <article className="qr-dynamic-block">
          <BlockTitle icon={Box} label="Product size" />

          <CustomSelect
            value={selectedSize || ''}
            options={product.available_sizes.map((option) => ({
              value: getOptionValue(option),
              label: getOptionLabel(option),
            }))}
            onChange={(value) => onSizeChange?.(value)}
          />
        </article>
      ) : null}

      <div className="qr-dynamic-panel__blocks">
        {enabledBlocks.map((block) => (
          <DynamicBlock
            key={block.id}
            block={block}
            value={getElement(configuration, block)}
            updateElement={(patch) =>
              updateElement(block, patch)
            }
            updateScalar={(value) =>
              updateScalarElement(block, value)
            }
            product={product}
            selectedMaterial={selectedMaterial}
            onMaterialChange={onMaterialChange}
            selectedColor={selectedColor}
            onColorChange={onColorChange}
            threeDConfiguration={threeDConfiguration}
            onThreeDChange={onThreeDChange}
          />
        ))}
      </div>

      <ThreeDBaseStyleBlock
        threeDConfiguration={threeDConfiguration}
        onThreeDChange={onThreeDChange}
      />
    </section>
  );
}

function DynamicBlock({
  block,
  value,
  updateElement,
  updateScalar,
  product,
  selectedMaterial,
  onMaterialChange,
  selectedColor,
  onColorChange,
  onThreeDChange,
}) {
  const settings = block.settings || {};

  if (block.type === 'text') {
    const textValue =
      value && typeof value === 'object'
        ? value
        : block.default || {};

    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={Type} label={block.label} />

        {settings.text_editable !== false ? (
          <label>
            <span>Text</span>
            <input
              value={textValue.text || ''}
              maxLength={settings.max_length || 120}
              onChange={(event) =>
                updateElement({
                  text: event.target.value,
                })
              }
            />
          </label>
        ) : null}

        <PositionControls
          settings={settings}
          value={textValue}
          onChange={updateElement}
        />

        <div className="qr-dynamic-grid">
          {settings.resizable ? (
            <EditorStepper
              label="Size"
              value={Number(textValue.font_size || 4)}
              min={settings.min_font_size || 1.8}
              max={settings.max_font_size || 9}
              step={0.1}
              suffix=""
              decimals={1}
              onChange={(nextValue) =>
                updateElement({
                  font_size: nextValue,
                })
              }
            />
          ) : null}

          {settings.color_editable ? (
            <label>
              <span>Color</span>
              <input
                type="color"
                value={textValue.color || '#1f2937'}
                onChange={(event) =>
                  updateElement({
                    color: event.target.value,
                  })
                }
              />
            </label>
          ) : null}
        </div>

        {settings.align_editable ? (
          <div className="qr-dynamic-align">
            {[
              ['left', AlignLeft],
              ['center', AlignCenter],
              ['right', AlignRight],
            ].map(([align, Icon]) => (
              <button
                type="button"
                key={align}
                className={
                  (textValue.align || 'center') === align
                    ? 'is-active'
                    : ''
                }
                onClick={() =>
                  updateElement({ align })
                }
                title={align}
              >
                <Icon size={14} />
              </button>
            ))}
          </div>
        ) : null}
      </article>
    );
  }

  if (block.type === 'qr') {
    const qrValue =
      value && typeof value === 'object'
        ? value
        : block.default || {};

    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={QrCode} label={block.label} />

        <PositionControls
          settings={settings}
          value={qrValue}
          onChange={updateElement}
        />

        {settings.resizable ? (
          <EditorStepper
            label="QR size"
            value={Number(qrValue.size || 34)}
            min={settings.min_size || 18}
            max={settings.max_size || 60}
            step={1}
            suffix="%"
            onChange={(nextValue) =>
              updateElement({
                size: nextValue,
              })
            }
          />
        ) : null}

        {settings.color_editable ? (
          <div className="qr-dynamic-grid">
            <label>
              <span>Foreground</span>
              <input
                type="color"
                value={
                  qrValue.foreground_color ||
                  '#111111'
                }
                onChange={(event) =>
                  updateElement({
                    foreground_color:
                      event.target.value,
                  })
                }
              />
            </label>

            <label>
              <span>Background</span>
              <input
                type="color"
                value={
                  qrValue.background_color ||
                  '#ffffff'
                }
                onChange={(event) =>
                  updateElement({
                    background_color:
                      event.target.value,
                  })
                }
              />
            </label>
          </div>
        ) : null}
      </article>
    );
  }

  if (block.type === 'nfc') {
    const nfcValue =
      value && typeof value === 'object'
        ? value
        : block.default || {};

    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={Nfc} label={block.label || 'NFC tap area'} />

        {settings.text_editable !== false ? (
          <label>
            <span>Tap text</span>
            <input
              value={nfcValue.text || ''}
              maxLength={80}
              onChange={(event) =>
                updateElement({ text: event.target.value })
              }
            />
          </label>
        ) : null}

        {settings.show_text_toggle !== false ? (
          <label className="qr-dynamic-check">
            <input
              type="checkbox"
              checked={nfcValue.show_text !== false}
              onChange={(event) =>
                updateElement({ show_text: event.target.checked })
              }
            />
            <span>Show tap instruction</span>
          </label>
        ) : null}

        <PositionControls
          settings={settings}
          value={nfcValue}
          onChange={updateElement}
        />

        {settings.resizable ? (
          <EditorStepper
            label="NFC icon size"
            value={Number(nfcValue.size || 18)}
            min={settings.min_size || 8}
            max={settings.max_size || 44}
            step={1}
            suffix="%"
            onChange={(nextValue) => updateElement({ size: nextValue })}
          />
        ) : null}

        {settings.color_editable ? (
          <label>
            <span>Color</span>
            <input
              type="color"
              value={nfcValue.color || '#111111'}
              onChange={(event) =>
                updateElement({ color: event.target.value })
              }
            />
          </label>
        ) : null}
      </article>
    );
  }

  if (
    block.type === 'logo' ||
    block.type === 'image'
  ) {
    return (
      <ImageBlock
        block={block}
        value={
          value && typeof value === 'object'
            ? value
            : block.default || {}
        }
        updateElement={updateElement}
      />
    );
  }

  if (block.type === 'product_color') {
    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={Palette} label={block.label || 'Product color'} />

        <div className="qr-dynamic-swatches">
          {(product?.available_colors || []).map((option) => {
            const optionValue = getOptionValue(option);
            const label = getOptionLabel(option);
            const hex =
              typeof option === 'object'
                ? option.hex
                : option;

            return (
              <button
                type="button"
                key={String(optionValue)}
                className={
                  String(selectedColor) ===
                  String(optionValue)
                    ? 'is-active'
                    : ''
                }
                onClick={() => {
                  onColorChange?.(optionValue);
                  updateScalar(optionValue);
                }}
              >
                <span
                  style={{
                    backgroundColor:
                      /^#[0-9a-f]{6}$/i.test(hex || '')
                        ? hex
                        : '#d8e2df',
                  }}
                />
                {label}
              </button>
            );
          })}
        </div>

        {settings.allow_custom_color ? (
          <label>
            <span>Custom color</span>
            <input
              type="color"
              value={
                /^#[0-9a-f]{6}$/i.test(selectedColor || '')
                  ? selectedColor
                  : '#51766e'
              }
              onChange={(event) => {
                onColorChange?.(event.target.value);
                updateScalar(event.target.value);
              }}
            />
          </label>
        ) : null}
      </article>
    );
  }

  if (block.type === 'background_color') {
    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={Palette} label={block.label} />

        <label>
          <span>Color</span>
          <input
            type="color"
            value={
              typeof value === 'string'
                ? value
                : '#ffffff'
            }
            onChange={(event) =>
              updateScalar(event.target.value)
            }
          />
        </label>
      </article>
    );
  }

  if (block.type === 'material') {
    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={Box} label={block.label} />

        <CustomSelect
          value={selectedMaterial || ''}
          options={(product?.available_materials || []).map(
            (option) => ({
              value: getOptionValue(option),
              label: getOptionLabel(option),
            }),
          )}
          onChange={(value) => {
            onMaterialChange?.(value);
            updateScalar(value);
          }}
        />
      </article>
    );
  }

  if (block.type === 'surface_finish') {
    const options =
      settings.options || [
        { value: 'glossy', label: 'Glossy' },
        { value: 'matte', label: 'Matte' },
        { value: 'frosted', label: 'Frosted' },
      ];

    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={Box} label={block.label} />

        <CustomSelect
          value={
            value === undefined || value === null
              ? block.default || 'glossy'
              : value
          }
          options={options}
          onChange={(nextValue) => {
            updateScalar(nextValue);
            onThreeDChange?.({
              surface_finish: nextValue,
            });
          }}
        />
      </article>
    );
  }

  if (block.type === 'three_d_property') {
    const current = Number(
      value === undefined || value === null
        ? block.default || 0
        : value,
    );

    return (
      <article className="qr-dynamic-block">
        <BlockTitle icon={Box} label={block.label} />

        <EditorStepper
          label={block.label}
          value={current}
          min={settings.min ?? 0}
          max={settings.max ?? 100}
          step={settings.step ?? 1}
          suffix={settings.unit || ''}
          onChange={(nextValue) => {
            updateScalar(nextValue);

            if (settings.property) {
              onThreeDChange?.({
                [settings.property]: nextValue,
              });
            }
          }}
        />
      </article>
    );
  }

  return null;
}

function ImageBlock({
  block,
  value,
  updateElement,
}) {
  const settings = block.settings || {};
  const fileRef = useRef(null);

  const handleFile = (file) => {
    if (!file || !file.type?.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      updateElement({
        src: String(reader.result || ''),
      });
    };

    reader.readAsDataURL(file);
  };

  return (
    <article className="qr-dynamic-block">
      <BlockTitle
        icon={block.type === 'logo' ? Upload : Image}
        label={block.label}
      />

      {settings.replaceable !== false ||
      settings.uploadable ? (
        <>
          <input
            ref={fileRef}
            className="qr-dynamic-hidden-file"
            type="file"
            accept="image/*"
            onChange={(event) =>
              handleFile(event.target.files?.[0])
            }
          />

          <button
            type="button"
            className="qr-dynamic-upload"
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={14} />
            {value.src ? 'Replace image' : 'Choose image'}
          </button>
        </>
      ) : null}


      <PositionControls
        settings={settings}
        value={value}
        onChange={updateElement}
      />

      {settings.resizable ? (
        <EditorStepper
          label="Width"
          value={Number(value.width || 24)}
          min={settings.min_width || 5}
          max={settings.max_width || 80}
          step={1}
          suffix="%"
          onChange={(nextValue) =>
            updateElement({
              width: nextValue,
            })
          }
        />
      ) : null}

      {settings.opacity_editable ? (
        <EditorStepper
          label="Opacity"
          value={Number(value.opacity ?? 1)}
          min={0.1}
          max={1}
          step={0.05}
          suffix="%"
          multiplier={100}
          decimals={0}
          onChange={(nextValue) =>
            updateElement({
              opacity: nextValue,
            })
          }
        />
      ) : null}
    </article>
  );
}

function ThreeDBaseStyleBlock({
  threeDConfiguration,
  onThreeDChange,
}) {
  if (!onThreeDChange) {
    return null;
  }

  return (
    <article className="qr-dynamic-block">
      <BlockTitle icon={Box} label="Base style" />

      <CustomSelect
        value={threeDConfiguration?.base_style || 'classic'}
        options={[
          {
            value: 'classic',
            label: 'Classic base',
          },
          {
            value: 'wide',
            label: 'Wide base',
          },
          {
            value: 'minimal',
            label: 'Minimal base',
          },
        ]}
        onChange={(value) =>
          onThreeDChange({
            base_style: value,
          })
        }
      />
    </article>
  );
}

function CustomSelect({
  value,
  options = [],
  onChange,
  placeholder = 'Select option',
}) {
  const [open, setOpen] = useState(false);

  const selected = options.find(
    (option) => String(option.value) === String(value),
  );

  return (
    <div
      className={`qr-dynamic-custom-select ${
        open ? 'is-open' : ''
      }`}
    >
      <button
        type="button"
        className="qr-dynamic-custom-select__trigger"
        data-custom-select="true"
        onClick={() => setOpen((current) => !current)}
      >
        <span>
          {selected?.label || placeholder}
        </span>

        <ChevronDown size={14} />
      </button>

      {open ? (
        <div className="qr-dynamic-custom-select__menu">
          {options.map((option) => (
            <button
              type="button"
              key={String(option.value)}
              className={
                String(option.value) === String(value)
                  ? 'is-selected'
                  : ''
              }
              onClick={() => {
                onChange?.(option.value);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>

              {String(option.value) === String(value) ? (
                <strong>✓</strong>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PositionControls({
  settings,
  value,
  onChange,
}) {
  if (!settings.movable) {
    return null;
  }

  return (
    <div className="qr-dynamic-position-grid">
      <EditorStepper
        label="X position"
        value={clamp(value.x ?? 50, 0, 100)}
        min={0}
        max={100}
        step={1}
        suffix="%"
        onChange={(nextValue) =>
          onChange({
            x: nextValue,
          })
        }
      />

      <EditorStepper
        label="Y position"
        value={clamp(value.y ?? 50, 0, 100)}
        min={0}
        max={100}
        step={1}
        suffix="%"
        onChange={(nextValue) =>
          onChange({
            y: nextValue,
          })
        }
      />
    </div>
  );
}

function EditorStepper({
  label,
  value,
  min = 0,
  max = 100,
  step = 1,
  suffix = '',
  multiplier = 1,
  decimals,
  onChange,
}) {
  const numericValue = Number(value || 0);
  const precision =
    decimals !== undefined
      ? decimals
      : String(step).includes('.')
        ? String(step).split('.')[1].length
        : 0;

  const clampValue = (nextValue) =>
    Math.min(
      Number(max),
      Math.max(Number(min), Number(nextValue)),
    );

  const commit = (nextValue) => {
    const cleanValue = Number(
      clampValue(nextValue).toFixed(precision),
    );

    onChange?.(cleanValue);
  };

  const displayValue = Number(
    (numericValue * multiplier).toFixed(decimals ?? precision),
  );

  return (
    <div className="qr-dynamic-stepper">
      <div className="qr-dynamic-stepper__top">
        <span>{label}</span>
        <strong>
          {displayValue}
          {suffix}
        </strong>
      </div>

      <div className="qr-dynamic-stepper__control">
        <button
          type="button"
          onClick={() => commit(numericValue - Number(step))}
          disabled={numericValue <= Number(min)}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>

        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={numericValue}
          onChange={(event) =>
            commit(event.target.value)
          }
        />

        <button
          type="button"
          onClick={() => commit(numericValue + Number(step))}
          disabled={numericValue >= Number(max)}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

function BlockTitle({ icon: Icon, label }) {
  return (
    <div className="qr-dynamic-block__title">
      <Icon size={14} />
      <strong>{label}</strong>
    </div>
  );
}
